import React, { useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Image,
  IconButton,
  useDisclosure,
  Collapse,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Text,
  useColorModeValue,
  BoxProps,
  Icon
} from '@chakra-ui/react';
import {
  FaBars,
  FaTimes,
  FaSignInAlt,
  FaTachometerAlt,
  FaSignOutAlt,
  FaHome,
  FaBox,
  FaInfoCircle,
  FaEnvelope,
  FaGraduationCap
} from 'react-icons/fa';
import { ChevronDownIcon } from '@chakra-ui/icons';
import AuthContext from '../services/AuthContext';

const Header = () => {
  const { isOpen, onToggle } = useDisclosure();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);

  // Design Tokens
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const headerBg = useColorModeValue('rgba(44, 62, 80, 0.95)', 'rgba(26, 32, 44, 0.95)');

  const handleLogout = () => {
    logout();
  };

  const navigationItems = [
    { name: 'الرئيسية', path: '/', icon: FaHome },
    { name: 'اكتشف المستويات', path: '/discover', icon: FaGraduationCap },
    { name: 'عروضنا', path: '/packs', icon: FaBox },
    { name: 'حول المنصة', path: '/about', icon: FaInfoCircle },
    { name: 'اتصل بنا', path: '/contact', icon: FaEnvelope }
  ];

  const NavButton = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <Button
        as={RouterLink}
        to={item.path}
        variant="ghost"
        color="white"
        _hover={{
          color: primaryColor,
          bg: 'whiteAlpha.100',
          transform: 'translateY(-2px)'
        }}
        _active={{ bg: 'whiteAlpha.200' }}
        fontWeight={isActive ? '800' : '600'}
        position="relative"
        transition="all 0.3s"
        fontFamily="Cairo, sans-serif"
        fontSize="md"
        px={4}
      >
        <Text ml={2}>{item.name}</Text>
        <Icon as={item.icon} boxSize={4} />
        {isActive && (
          <Box
            position="absolute"
            bottom="-2px"
            left="50%"
            transform="translateX(-50%)"
            width="20px"
            height="3px"
            bg={primaryColor}
            borderRadius="full"
          />
        )}
      </Button>
    );
  };

  return (
    <Box
      bg={headerBg}
      backdropFilter="blur(10px)"
      color="white"
      py={3}
      borderBottom={`3px solid ${primaryColor}`}
      position="sticky"
      top={0}
      zIndex="sticky"
      boxShadow="xl"
      dir="rtl"
    >
      <Container maxW="1400px">
        <Flex justify="space-between" align="center">
          {/* Logo */}
          <RouterLink to="/">
            <HStack spacing={3}>
              <Image
                src="https://i.imgur.com/EFLnpkN.png"
                alt="منصة كوبرا التعليمية"
                h={{ base: "40px", md: "55px" }}
                transition="transform 0.3s"
                _hover={{ transform: 'scale(1.05)' }}
              />
            </HStack>
          </RouterLink>

          {/* Desktop Navigation */}
          <HStack spacing={2} display={{ base: 'none', lg: 'flex' }}>
            {navigationItems.map((item) => (
              <NavButton key={item.path} item={item} />
            ))}
          </HStack>

          {/* User Actions & Mobile Toggle */}
          <HStack spacing={4}>
            {/* Desktop User Actions */}
            <Box display={{ base: 'none', lg: 'block' }}>
              {user ? (
                <Menu gutter={12}>
                  <MenuButton
                    as={Button}
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.100' }}
                    _active={{ bg: 'whiteAlpha.200' }}
                    leftIcon={<ChevronDownIcon />}
                  >
                    <HStack spacing={3} flexDirection="row-reverse">
                      <Avatar
                        size="sm"
                        name={user.name}
                        border={`2px solid ${primaryColor}`}
                        bg={primaryColor}
                        color={secondaryColor}
                        fontWeight="bold"
                      />
                      <VStack align="flex-end" spacing={0}>
                        <Text fontFamily="Cairo, sans-serif" fontWeight="800" fontSize="sm">{user.name}</Text>
                      </VStack>
                    </HStack>
                  </MenuButton>
                  <MenuList
                    bg="white"
                    color={secondaryColor}
                    border={`1px solid ${primaryColor}`}
                    borderRadius="xl"
                    boxShadow="2xl"
                    p={2}
                  >
                    <MenuItem
                      as={RouterLink}
                      to={user.role === "instructor" ? "/instructor/courses" : user.role === "admin" ? "/admin/users" : "/courses"}
                      icon={<FaTachometerAlt />}
                      _hover={{ bg: `${primaryColor}15` }}
                      borderRadius="lg"
                      fontFamily="Cairo, sans-serif"
                      fontWeight="700"
                      mb={1}
                    >
                      لوحة التحكم التعليمية
                    </MenuItem>
                    <MenuItem
                      onClick={handleLogout}
                      icon={<FaSignOutAlt />}
                      _hover={{ bg: 'red.50', color: 'red.600' }}
                      borderRadius="lg"
                      fontFamily="Cairo, sans-serif"
                      fontWeight="700"
                    >
                      تسجيل الخروج
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : (
                <Button
                  as={RouterLink}
                  to="/login"
                  bg={primaryColor}
                  color={secondaryColor}
                  _hover={{ bg: '#d4b016', transform: 'translateY(-2px)' }}
                  _active={{ transform: 'translateY(0)' }}
                  fontFamily="Cairo, sans-serif"
                  fontWeight="900"
                  px={8}
                  borderRadius="xl"
                  boxShadow="lg"
                  leftIcon={<FaSignInAlt />}
                >
                  دخول التلاميذ
                </Button>
              )}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              display={{ base: 'flex', lg: 'none' }}
              onClick={onToggle}
              icon={isOpen ? <FaTimes /> : <FaBars />}
              variant="ghost"
              aria-label="تبديل القائمة"
              color="white"
              _hover={{ bg: 'whiteAlpha.200' }}
              fontSize="xl"
            />
          </HStack>
        </Flex>

        {/* Mobile Navigation */}
        <Collapse in={isOpen} animateOpacity>
          <VStack
            spacing={2}
            py={6}
            align="stretch"
            display={{ lg: 'none' }}
          >
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                as={RouterLink}
                to={item.path}
                variant="ghost"
                color="white"
                _hover={{ bg: 'whiteAlpha.100', color: primaryColor }}
                justifyContent="flex-start"
                rightIcon={<Icon as={item.icon} />}
                fontFamily="Cairo, sans-serif"
                fontWeight="700"
                h="50px"
                fontSize="md"
                onClick={onToggle}
              >
                {item.name}
              </Button>
            ))}

            <Box pt={4} borderTop="1px solid" borderColor="whiteAlpha.200">
              {user ? (
                <VStack spacing={2} align="stretch">
                  <Button
                    as={RouterLink}
                    to={user.role === "instructor" ? "/instructor/courses" : user.role === "admin" ? "/admin/users" : "/courses"}
                    variant="solid"
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#d4b016' }}
                    justifyContent="flex-start"
                    rightIcon={<FaTachometerAlt />}
                    fontFamily="Cairo, sans-serif"
                    fontWeight="800"
                    h="50px"
                    onClick={onToggle}
                  >
                    لوحة التحكم
                  </Button>
                  <Button
                    onClick={() => { handleLogout(); onToggle(); }}
                    variant="ghost"
                    color="red.300"
                    _hover={{ bg: 'red.500', color: 'white' }}
                    justifyContent="flex-start"
                    rightIcon={<FaSignOutAlt />}
                    fontFamily="Cairo, sans-serif"
                    fontWeight="800"
                    h="50px"
                  >
                    تسجيل الخروج
                  </Button>
                </VStack>
              ) : (
                <Button
                  as={RouterLink}
                  to="/login"
                  bg={primaryColor}
                  color={secondaryColor}
                  w="100%"
                  h="50px"
                  fontFamily="Cairo, sans-serif"
                  fontWeight="900"
                  rightIcon={<FaSignInAlt />}
                  onClick={onToggle}
                >
                  دخول التلاميذ
                </Button>
              )}
            </Box>
          </VStack>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Header;
